"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useLang } from "@/components/LangProvider";

type NavKey =
  | "brand"
  | "agriculture"
  | "news"
  | "company"
  | "contact";

type NavItem = {
  key: NavKey;
  href: string;
};

const navItems: NavItem[] = [
  { key: "brand", href: "/#brand" },
  { key: "agriculture", href: "/#agriculture" },
  { key: "news", href: "/#news" },
  { key: "company", href: "/#company" },
  { key: "contact", href: "/contact" },
];

function CartIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function NoteIcon({ className }: { className?: string }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src="/v2/icon/note.svg" alt="note" className={className} />;
}

function LangToggle({ className = "" }: { className?: string }) {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full border border-foreground/15 px-3 py-1 font-latin text-foreground/85 ${className}`}
      style={{ fontSize: 12, letterSpacing: "0.1em" }}
      role="group"
      aria-label="Language toggle"
    >
      <button
        type="button"
        onClick={() => setLang("jp")}
        aria-pressed={lang === "jp"}
        className={`px-1 transition-colors ${
          lang === "jp" ? "text-accent-forest font-semibold" : "text-foreground/55 hover:text-foreground"
        }`}
      >
        JP
      </button>
      <span className="text-foreground/30">/</span>
      <button
        type="button"
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
        className={`px-1 transition-colors ${
          lang === "en" ? "text-accent-forest font-semibold" : "text-foreground/55 hover:text-foreground"
        }`}
      >
        EN
      </button>
    </div>
  );
}

export function Header() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-rule/70"
      style={{ background: "rgba(248, 247, 244, 0.86)" }}
    >
      <div className="mx-auto max-w-site px-6 md:px-14 h-16 md:h-[68px] flex items-center justify-between gap-4">
        <Link href="/" className="relative z-10 py-2 inline-flex items-center" aria-label="Plum Town">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logo_tr.svg"
            alt="Plum Town"
            className="h-[26px] md:h-[28px] w-auto opacity-[0.78] hover:opacity-100 transition-opacity duration-200"
          />
        </Link>

        <nav className="hidden lg:flex items-center justify-center gap-7 text-foreground font-sans font-medium" style={{ fontSize: 11.5, letterSpacing: "0.2em", textTransform: "uppercase" }}>
          {navItems.map((item) => (
            <Link
              key={item.key}
              href={item.href}
              className="relative py-1.5 opacity-[0.78] hover:opacity-100 hover:text-accent-forest transition-all duration-200 after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-px after:bg-accent-forest after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform after:duration-300"
            >
              <span className="relative inline-flex items-center">
                {t.nav[item.key]}
              </span>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3.5">
          <LangToggle className="hidden md:inline-flex" />

          <a
            href="https://note.com/plumtown"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="note"
            className="hidden md:inline-flex w-8 h-8 items-center justify-center rounded-full text-foreground opacity-70 hover:opacity-100 hover:text-accent-forest hover:bg-accent-forest/10 transition-all duration-200"
          >
            <NoteIcon className="w-[18px] h-[18px]" />
          </a>
          <a
            href="https://www.instagram.com/soga_craft_umeshu/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="hidden md:inline-flex w-8 h-8 items-center justify-center rounded-full text-foreground opacity-70 hover:opacity-100 hover:text-accent-forest hover:bg-accent-forest/10 transition-all duration-200"
          >
            <InstagramIcon className="w-[18px] h-[18px]" />
          </a>
          <a
            href="https://shop.plum-town.jp/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.shopButton}
            className="hidden md:inline-flex items-center gap-2 bg-accent-forest text-white px-4 py-2.5 rounded-full font-sans font-medium hover:bg-accent-forest2 hover:-translate-y-px transition-all duration-200 whitespace-nowrap"
            style={{
              fontSize: 12,
              letterSpacing: "0.16em",
              boxShadow: "0 6px 18px -8px rgba(31, 58, 43, 0.7)",
            }}
          >
            <CartIcon className="w-[15px] h-[15px]" />
            <span>{t.shopButton}</span>
          </a>
          <a
            href="https://shop.plum-town.jp/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.shopButton}
            className="md:hidden text-foreground hover:text-accent-forest transition-colors"
          >
            <CartIcon className="w-5 h-5" />
          </a>

          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden relative w-9 h-9 flex flex-col items-center justify-center gap-[5px] text-[#1A1A1A] hover:text-accent-forest transition-colors"
          >
            <span
              className={`block h-px w-5 bg-current transition-transform duration-300 ${
                menuOpen ? "translate-y-[6px] rotate-45" : ""
              }`}
            />
            <span
              className={`block h-px w-5 bg-current transition-opacity duration-300 ${
                menuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block h-px w-5 bg-current transition-transform duration-300 ${
                menuOpen ? "-translate-y-[6px] -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          menuOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setMenuOpen(false);
        }}
        aria-hidden={!menuOpen}
      >
        <div className="absolute inset-0 bg-[#1A1A1A]/30 backdrop-blur-sm" />
        <div
          className={`absolute top-0 right-0 h-full w-[88%] max-w-sm bg-[#FAFAF8] shadow-2xl transition-transform duration-300 ease-out ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="h-16 md:h-20 flex items-center justify-end px-6">
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
              className="w-9 h-9 flex flex-col items-center justify-center gap-[5px] text-[#1A1A1A]"
            >
              <span className="block h-px w-5 bg-current translate-y-[6px] rotate-45" />
              <span className="block h-px w-5 bg-current opacity-0" />
              <span className="block h-px w-5 bg-current -translate-y-[6px] -rotate-45" />
            </button>
          </div>

          <nav className="px-8 pt-4 pb-8 flex flex-col">
            {navItems.map((item, i) => (
              <Link
                key={item.key}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-baseline gap-[18px] py-5 border-b border-rule text-foreground hover:text-accent-forest hover:pl-1.5 transition-all duration-200"
              >
                <span
                  className="font-latin italic text-mute min-w-[32px] flex-shrink-0"
                  style={{ fontSize: 13, letterSpacing: "0.1em" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className="font-display font-medium inline-flex items-baseline gap-2"
                  style={{ fontSize: 22, letterSpacing: "0.06em" }}
                >
                  {t.nav[item.key]}
                </span>
              </Link>
            ))}
          </nav>

          <div className="px-8 pt-2 pb-10 mt-4 border-t border-neutral-200/60 space-y-6">
            <LangToggle />

            <div className="flex items-center gap-5 text-[#1A1A1A]">
              <a
                href="https://note.com/plumtown"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="note"
                className="hover:text-accent-forest transition-colors"
              >
                <NoteIcon className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/soga_craft_umeshu/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="hover:text-accent-forest transition-colors"
              >
                <InstagramIcon className="w-5 h-5" />
              </a>
            </div>

            <a
              href="https://shop.plum-town.jp/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 w-full justify-center bg-accent-forest text-white px-6 py-3.5 text-xs tracking-[0.25em] uppercase font-medium hover:opacity-90 transition-opacity"
            >
              <CartIcon className="w-4 h-4" />
              <span>{t.shopButton}</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
